import { getApiClient } from "@src/api";
import { graphql } from "@src/gql/gql";
import { ReferralStatus } from "@src/gql/graphql";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { z } from "zod";

// Zod schemas for validation
const rewardSchema = z.object({
	expiresAt: z.string(),
	grantedAt: z.string(),
	isRedeemed: z.boolean(),
	rewardCode: z.string(),
	refereeId: z.string(),
	rewardType: z.string().nullable(),
});

const referralInfoSchema = z.object({
	activatedAt: z.string().nullable(),
	registeredAt: z.string().nullable(),
	refereeEmail: z.string(),
	refereeId: z.string(),
	status: z.nativeEnum(ReferralStatus),
});

const referralProfileSchema = z.object({
	qualifiedReferralCount: z.number(),
	totalRewardsGranted: z.number(),
	totalRewardsRedeemed: z.number(),
	rewards: z.array(rewardSchema).nullable(),
	referralCode: z.string().nullable(),
	referredBy: z.string().nullable(),
	referredByEmail: z.string().nullable(),
	referrals: z.array(referralInfoSchema).nullable(),
});

// Export types
export type Reward = z.infer<typeof rewardSchema>;
export type ReferralInfo = z.infer<typeof referralInfoSchema>;
export type ReferralProfile = z.infer<typeof referralProfileSchema>;

export const referralProfileQueryKeys = {
	all: ["myReferralProfile"] as const,
};

const getMyReferralProfileQuery = graphql(/* GraphQL */ `
	query GetMyReferralProfile {
		referrals {
			profile {
				qualifiedReferralCount
				totalRewardsGranted
				totalRewardsRedeemed
				rewards {
					expiresAt
					grantedAt
					isRedeemed
					rewardCode
					refereeId
                    rewardType
				}
				referralCode
				referredBy
				referredByEmail
				referrals {
					activatedAt
					registeredAt
					refereeEmail
					refereeId
					status
				}
			}
		}
	}
`);

/**
 * Fetch the referral profile for the current user
 * @returns Validated referral profile
 */
export async function fetchMyReferralProfile(): Promise<ReferralProfile | null> {
	const client = getApiClient();

	const response = await client.query({
		query: getMyReferralProfileQuery,
	});

	const profile = response.data?.referrals?.profile;

	if (!profile) {
		return null;
	}

	return referralProfileSchema.parse(profile);
}

const myReferralProfileQueryOptions = queryOptions({
	queryKey: referralProfileQueryKeys.all,
	queryFn: () => fetchMyReferralProfile(),
	staleTime: 5 * 60 * 1000,
});

export function useMyReferralProfile() {
	return useQuery(myReferralProfileQueryOptions);
}
